import React from "react";
import { motion } from "framer-motion";
import styles from "./HomeIntroHero.module.css";
import ProductCard from "../Products/ProductCard";
import { IHomeFeaturedProductsSectionProps } from "@model/Home.model";
import { useMotion } from "../../../Logic/Functions/Hooks/useMotion";
import useImageUploadRules from "../../../Logic/Functions/Hooks/useImageUploadRules";
import Carousel from "../UILibrary/Carousel";

const HomeFeaturedProductsSection: React.FC<IHomeFeaturedProductsSectionProps> = ({
  products,
  title = "Designed for the Way You Live",
  subtitle = "A smart-home controller built to simplify everyday comfort. From lighting and curtains to climate and home routines,Chimera brings the essentials of your space into one beautifully connected experience.",
  once = false,
  triggerAmount = 0.2,
}) => {
  const { slideFade, containerStagger } = useMotion();

  const { buildImageSrc } = useImageUploadRules({
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  if (!products || products.length === 0) return null;

  return (
    <section
      className={styles.HFPSection}
      aria-labelledby="home-featured-products-title"
      data-testid="home-featured-products"
    >
      <motion.header
        className={styles.HFPHeader}
        variants={containerStagger(0.12)}
        initial="hidden"
        whileInView="show"
        viewport={{ once, amount: triggerAmount }}
      >
        <motion.h2
          id="home-featured-products-title"
          className={styles.HFPTitle}
          variants={slideFade("left", 0)}
        >
          {title}
        </motion.h2>

        {subtitle && (
          <motion.p
            className={styles.HFPSubtitle}
            variants={slideFade("right", 0.06)}
          >
            {subtitle}
          </motion.p>
        )}
      </motion.header>

      <motion.div
        variants={containerStagger(0.15)}
        initial="hidden"
        whileInView="show"
        viewport={{ once, amount: triggerAmount }}
      >
        <Carousel
          step={0.9}
          className={styles.HFPCarousel ?? ""}
          slideClassName={styles.HFPCardWrapper}
        >
          {products.map((product: any, i) => {
            const firstRaw =
              Array.isArray(product?.images) && product.images.length
                ? typeof product.images[0] === "string"
                  ? product.images[0]
                  : product.images[0]?.url
                : product?.imageUrl;

            const normalized = buildImageSrc(firstRaw ?? null) ?? "";

            return (
              <motion.div
                key={product.id}
                variants={slideFade("up", 0.04 * i)}
              >
                <ProductCard {...(product)} imageUrl={normalized} />
              </motion.div>
            );
          })}
        </Carousel>
      </motion.div>
    </section>
  );
};

export default HomeFeaturedProductsSection;
